import { RootState } from "@app/types"
import React, { useCallback, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { mapPreparationTime } from "@modules/recipes/helpers"
import CupcakeIcon from "@resources/cupcake"
import getDay from "date-fns/getDay"
import { getWeekMenu } from "./store/actions/weekmenuActions"

import Spinner from "@ng-mw/reol/components/Spinner"
import Tabs, { Tab } from "@ng-mw/reol/components/Tabs"
import type { Swiper as SwiperInstance } from "swiper"
import "swiper/css"
import { A11y } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import RecipeLink from "./RecipeLink"
import RecipeTime from "./RecipeTime"

import { trackClickWeekday, useAutomaticTrackingData } from "@ng-mw/shared-react-components"
import "./style/ultra-compact-weekmenu.scss"

interface UltraCompactWeekMenuProps {
    headingLevel?: number
}

const UltraCompactWeekMenu = ({ headingLevel = 2 }: UltraCompactWeekMenuProps) => {
    const dispatch = useDispatch<any>()
    const weekMenuData = useSelector((state: RootState) => state.weekmenu)

    const automaticTrackingData = useAutomaticTrackingData()

    useEffect(() => {
        dispatch(getWeekMenu())
    }, [dispatch])

    const [swiper, setSwiper] = useState<SwiperInstance | null>(null)
    const today = new Date()

    const SubHeading = `h${headingLevel + 1}` as React.ElementType

    // getDay return sunday as 0 but we want monday as 0 and sunday as 1
    /*
    (0 + 6) % 7 = 6
    (1 + 6) % 7 = 0
    (2 + 6) % 7 = 1
    (3 + 6) % 7 = 2
    (4 + 6) % 7 = 3
    (5 + 6) % 7 = 4
    (6 + 6) % 7 = 5
    */
    const initial = (6 + getDay(today)) % 7
    // Ignore checking year and weeknumber since this week menu always shows the current week
    const [activeDay, setActiveDay] = React.useState(initial)

    const recipeList = weekMenuData.data[0]?.RecipeList
    const handleWeekdayClick = useCallback(
        (dayIndex: number) => {
            setActiveDay(dayIndex)
            trackClickWeekday(
                automaticTrackingData,
                recipeList[dayIndex].Title,
                recipeList[dayIndex].RecipeId,
                recipeList[dayIndex].WeekDayShort,
            )
        },
        [recipeList, automaticTrackingData],
    )

    useEffect(() => {
        if (weekMenuData.data.length > 0 && swiper && !swiper.destroyed) {
            swiper.slideTo(activeDay)
        }
    }, [swiper, activeDay, weekMenuData])

    if (weekMenuData.loading) {
        return (
            <div className="week-menu-wrapper week-menu-wrapper--loading">
                <Spinner />
            </div>
        )
    }
    if (weekMenuData.data.length < 1) {
        return null
    }

    const maxNum = recipeList.length

    const activeIndexWrapped = ((activeDay % maxNum) + maxNum) % maxNum
    return (
        <div className="ultra-compact-weekmenu">
            <div className="ultra-compact-weekmenu__tabwrapper">
                <Tabs
                    className="ultra-compact-weekmenu__tabs"
                    onChange={(_e: any, value: number) => handleWeekdayClick(value)}
                    value={activeIndexWrapped}
                >
                    {recipeList.map(recipe => (
                        <Tab
                            key={`tab_${recipe.WeekDayShort}`}
                            label={recipe.WeekDayShort}
                            title={recipe.WeekDay}
                            aria-label={recipe.WeekDay}
                        />
                    ))}
                </Tabs>
                <Tabs
                    className="ultra-compact-weekmenu__tiny-tabs"
                    onChange={(_e: any, value: number) => handleWeekdayClick(value)}
                    value={activeIndexWrapped}
                >
                    {recipeList.map(recipe => (
                        <Tab
                            key={`tab_${recipe.WeekDayShort}`}
                            label={recipe.WeekDayShort.slice(0, 1)}
                            icon={recipe.WeekDayShort === "Bakst" ? CupcakeIcon : undefined}
                            title={recipe.WeekDay}
                            aria-label={recipe.WeekDay}
                        />
                    ))}
                </Tabs>
            </div>
            <div className="ultra-compact-weekmenu__recipeimages">
                <Swiper
                    modules={[A11y]}
                    spaceBetween={16}
                    slidesOffsetAfter={16}
                    slidesOffsetBefore={16}
                    slidesPerView={1.2}
                    initialSlide={activeDay}
                    onSwiper={s => setSwiper(s)}
                    onSlideChange={s => setActiveDay(s.realIndex ?? s.activeIndex)}
                    onBeforeDestroy={() => setSwiper(null)}
                >
                    {recipeList.map(recipe => (
                        <SwiperSlide
                            key={`img_${recipe.WeekDay}`}
                            className="ultra-compact-weekmenu__card"
                            aria-labelledby={`thelink_${recipe.WeekDay}`}
                        >
                            <RecipeLink
                                recipeId={recipe.RecipeId}
                                recipeName={recipe.Title}
                                aria-hidden="true"
                            >
                                <img
                                    src={recipe.Image}
                                    alt=""
                                    width="112"
                                    height="114"
                                />
                            </RecipeLink>
                            <div className="ultra-compact-weekmenu__texts">
                                <RecipeLink
                                    recipeId={recipe.RecipeId}
                                    recipeName={recipe.Title}
                                    id={`thelink_${recipe.WeekDay}`}
                                >
                                    <SubHeading className="c-h5">{recipe.Title}</SubHeading>
                                </RecipeLink>
                                <RecipeTime time={mapPreparationTime(recipe.TimeEstimate)} />
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </div>
    )
}

export default UltraCompactWeekMenu
